import { TagColor } from '../../muiComponents/Tag';
import {
    defaultFilterTypes,
    headerColumnTypes,
    ITableHeaderColumn,
    sortDirections,
} from '../../types/lists/listTypes';
import {
    ITaxComplementaryDataOption,
    ITaxesFiltersState,
    ITaxState,
    ParsedRuleType,
} from '../../types/taxes/taxesTypes';
import { commonIcons } from '../commonVariables/commonIcons';
import { getRandomNumber } from '../usefulFunctions';

export const getFiltersInitialState = (
    timeZone: string
): ITaxesFiltersState => ({
    Name: {
        filterType: defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE,
        filterKey: 'Name',
        value: '',
    },
    IdSupplierHotel: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'IdSupplierHotel',
    },
    IdSupplierHotelRoomType: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'IdSupplierHotelRoomType',
    },
    IdSupplierHotelMealPlan: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'IdSupplierHotelMealPlan',
    },
    IdSupplierHotelRatePlan: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'IdSupplierHotelRatePlan',
    },
    FromDate: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_DATE_TIME_FROM_FILTER_TYPE,
        filterKey: 'FromDate',
        timeZone: timeZone,
    },
    ToDate: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_DATE_TIME_TO_FILTER_TYPE,
        filterKey: 'ToDate',
        timeZone: timeZone,
    },
});

export const TAX_INITIAL_STATE: ITaxState = {
    Id: `-${getRandomNumber()}`,
    Enabled: true,
    Name: '',
    Amount: 0,
    Priority: 0,
    Description: '',
    IdSupplierHotel: 0,
    SupplierHotel: '',
    IdSupplierHotelRoomType: 0,
    SupplierHotelRoomType: '',
    IdSupplierHotelMealPlan: 0,
    SupplierHotelMealPlan: '',
    IdSupplierHotelRatePlan: 0,
    SupplierHotelRatePlan: '',
    IdSupplierTaxType: 0,
    SupplierTaxType: '',
    SupplierTaxTypeKey: '',
    IdSupplierTaxCalculationType: 0,
    SupplierTaxCalculationType: '',
    SupplierTaxCalculationTypeKey: '',
    IdSupplierTaxCalculationRuleType: 0,
    SupplierTaxCalculationRuleType: '',
    SupplierTaxCalculationRuleTypeKey: '',
    IdSupplementCalculationType: 0,
    SupplementCalculationType: '',
    SupplementCalculationTypeKey: '',
    IdCurrency: 0,
    Currency: '',
    EffectivePerson: 0,
    FromAge: 0,
    ToAge: 0,
    OnlyAdults: false,
    OnlyChildren: false,
    FromDate: null,
    ToDate: null,
    LastUpdate: null,
};

export const taxesTableColumns: ITableHeaderColumn[] = [
    {
        id: 'Enabled',
        labelText: 'enabled',
        titleText: 'enabled',
    },
    {
        id: 'Name',
        labelText: 'name',
        titleText: 'name',
        columnType: headerColumnTypes.SORTABLE,
        sortableProps: {
            sortKey: 'Name',
            defaultSortDir: sortDirections.ASC,
        },
    },
    {
        id: 'SupplierHotel/Name',
        labelText: 'hotel',
        titleText: 'hotel',
        columnType: headerColumnTypes.SORTABLE,
        sortableProps: {
            sortKey: 'SupplierHotel/Name',
            defaultSortDir: sortDirections.ASC,
        },
    },
    {
        id: 'SupplierHotelRoomType/Name',
        labelText: 'room',
        titleText: 'room',
        columnType: headerColumnTypes.SORTABLE,
        sortableProps: {
            sortKey: 'SupplierHotelRoomType/Name',
            defaultSortDir: sortDirections.ASC,
        },
    },
    {
        id: 'SupplierHotelMealPlan/Name',
        labelText: 'meal_plan',
        titleText: 'meal_plan',
        columnType: headerColumnTypes.SORTABLE,
        sortableProps: {
            sortKey: 'SupplierHotelMealPlan/Name',
            defaultSortDir: sortDirections.ASC,
        },
    },
    {
        id: 'SupplierHotelRatePlan/Name',
        labelText: 'rate_plan',
        titleText: 'rate_plan',
        columnType: headerColumnTypes.SORTABLE,
        sortableProps: {
            sortKey: 'SupplierHotelRatePlan/Name',
            defaultSortDir: sortDirections.ASC,
        },
    },
    { id: 'Type', labelText: 'type', titleText: 'type' },
    {
        id: 'FromDate',
        labelText: 'dates',
        titleText: 'dates',
    },
];

export const filterSingleColumnGrid = { xs: 12, sm: 12, md: 6, lg: 3, xl: 3 };
export const filterDoubleColumnGrid = { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 };

export const panelColumnsSpacing = {
    xs: '30px',
    sm: '30px',
    lg: '30px',
    xl: '30px',
};

export const panelRowsSpacing = {
    xs: '30px',
    sm: '30px',
    lg: '30px',
    xl: '30px',
};

export const panelTaxFullSizeFieldColumnGrid = {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
};
export const panelTaxDoubleFieldColumnGrid = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6,
};

export const panelTaxNameFieldColumnGrid = {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 6,
    xl: 6,
};

export const panelTaxSingleFieldColumnGrid = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 3,
    xl: 3,
};

export const supplierTaxTypesIcons = {
    INCLUSIVE: 'CheckCircle',
    EXCLUSIVE: 'AddCircleOutline',
    OTHER: 'PendingOutlined',
};

export const supplierTaxTypesOrder = ['INCLUSIVE', 'EXCLUSIVE', 'OTHER'];

export const supplierTaxCalculationTypesIcons = {
    PERCENT: 'PercentOutlined',
    FIXED: 'PinOutlined',
};

export const supplierTaxCalculationTypesOrder = ['FIXED', 'PERCENT'];

export const supplierTaxCalculationRuleTypeSubjectIcons = {
    PERSON: 'Person',
    ROOM: commonIcons.ROOM,
    BOOKING: 'ConfirmationNumber',
    OTHER: 'MoreHoriz',
};

export const supplierTaxCalculationRuleTypeAppliesByOrder = [
    'PERSON',
    'ROOM',
    'BOOKING',
    'OTHER',
];

export const supplierTaxCalculationRuleTypeFrequencyIcons = {
    NIGHT: 'Nightlight',
    STAY: 'Luggage',
    WEEK: 'DateRange',
};

export const supplierTaxCalculationRuleTypeFrequencyOrder = [
    'NIGHT',
    'WEEK',
    'STAY',
];

export const supplementCalculationTypeIcons = {
    NONE: 'StorefrontOutlined',
    STATIC: 'PlaylistAddOutlined',
    DYNAMIC: 'PlaylistAddCheckOutlined',
};

export const supplementCalculationTypeOrder = ['NONE', 'STATIC', 'DYNAMIC'];

export const UnknownOptionIcons = {
    SUPPLIER_TAX_TYPE: 'Album',
    SUPPLIER_TAX_CALCULATION_TYPE: 'MoreHoriz',
    SUPPLIER_TAX_CALCULATION_RULE_SUBJECT_TYPE: 'MoreHoriz',
    SUPPLIER_TAX_CALCULATION_RULE_FREQUENCY_TYPE: 'MoreHoriz',
    SUPPLEMENT_CALCULATION_TYPE: 'RemoveCircleOutline',
};

export const getSupplierTaxCalculationRuleTypeSubTypes = (ruleKey: string) => {
    const match = ruleKey.match(/^PER([A-Z]+)PER([A-Z]+)$/);
    if (match) {
        const [, subject, frequency] = match;
        return {
            subject,
            frequency,
        };
    } else {
        return { other: ruleKey };
    }
};

export const formatSupplierTaxCalculationRuleTypes = (
    ruleTypes: ITaxComplementaryDataOption[]
) => {
    const subjects = new Set<string>();
    const frequencies = new Set<string>();
    const parsedRules: ParsedRuleType[] = [];
    const otherRules: ITaxComplementaryDataOption[] = [];

    ruleTypes.forEach(rule => {
        const match = rule.Key?.match(/^PER([A-Z]+)PER([A-Z]+)$/);
        if (match) {
            const [, subject, frequency] = match;
            subjects.add(subject.toUpperCase());
            frequencies.add(frequency.toUpperCase());

            parsedRules.push({
                subject: subject.toUpperCase(),
                frequency: frequency.toUpperCase(),
                original: rule,
            });
        } else {
            otherRules.push(rule);
        }
    });
    return {
        subjects: Array.from(subjects).sort(),
        frequencies: Array.from(frequencies).sort(),
        parsedRules,
        otherRules,
    };
};

export const TAX_DATE_TAG_COLOR: Record<string, TagColor> = {
    old: 'cyan',
    current: 'green',
    upcoming: 'purple',
    dateless: 'yellow',
};

export const PANEL_VALIDATION_INITIAL_STATE = {
    isValidated: false,
    Name: '',
    ToDate: '',
    IdSupplierHotel: '',
    IdSupplierTaxType: '',
    IdSupplierTaxCalculationType: '',
    IdSupplementCalculationType: '',
    AppliesBy: '',
    Frequency: '',
    IdCurrency: '',
};
